import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Plus, Trash2, Loader2, Check, Pencil,
  Layers, LayoutGrid, Settings, DollarSign, CalendarDays,
  Eye, Play, Pause, RefreshCw, Zap, Bot, Send, X,
  Maximize2, Grid3X3, Building2, ChevronRight, AlertTriangle
} from "lucide-react";
import { CoPilotFieldHelp } from "@/components/CoPilotFieldHelp";

// ═══ CONSTANTS ═══
const SPACE_TYPES = [
  { value: "covered_court", label: "Covered Court" },
  { value: "indoor_court", label: "Indoor Court" },
  { value: "outdoor_court", label: "Outdoor Court" },
  { value: "studio", label: "Studio" },
  { value: "multipurpose_hall", label: "Multipurpose Hall" },
  { value: "training_area", label: "Training Area" },
  { value: "open_field", label: "Open Field" },
  { value: "swimming_pool", label: "Swimming Pool" },
  { value: "billiard_pool_hall", label: "Billiard / Pool Hall" },
  { value: "bar", label: "Bar" },
  { value: "room", label: "Room" },
  { value: "pond", label: "Pond" },
  { value: "range", label: "Range" },
];
const SURFACE_TYPES = [
  { value: "concrete", label: "Concrete" }, { value: "wood", label: "Wood" },
  { value: "rubberized", label: "Rubberized" }, { value: "synthetic", label: "Synthetic" },
  { value: "tatami", label: "Tatami" }, { value: "turf", label: "Turf" },
  { value: "tile", label: "Tile" }, { value: "not_applicable", label: "Not Applicable" },
];
const CLASSIFICATIONS = [
  { value: "single_sport", label: "Single-Sport Court", icon: Maximize2, color: "text-blue-600 bg-blue-50 border-blue-200", desc: "Dedicated to one sport only. Full court, no splits." },
  { value: "multi_sport", label: "Multi-Sport Court", icon: Grid3X3, color: "text-orange-600 bg-orange-50 border-orange-200", desc: "Multiple activities with optional half-court splits." },
  { value: "facility", label: "Facility / Studio", icon: Building2, color: "text-purple-600 bg-purple-50 border-purple-200", desc: "Indoor rooms for classes, fitness, martial arts." },
];
const BOOKING_MODES = [
  { value: "instant", label: "Instant Book" },
  { value: "request_only", label: "Request Only (Requires Approval)" },
  { value: "owner_scheduled_only", label: "Owner Scheduled Only" },
];
const NOISE_CLASSES = [
  { value: "low", label: "Low" }, { value: "medium", label: "Medium" }, { value: "high", label: "High" },
];
const APPROVAL_MODES = [
  { value: "none", label: "None" },
  { value: "rate_threshold", label: "Rate Threshold" },
  { value: "opportunity_cost_threshold", label: "Opportunity Cost" },
];

// ═══════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════
export default function SpaceManager() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [venue, setVenue] = useState(null);
  const [spaces, setSpaces] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [peakProfiles, setPeakProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("overview"); // overview | declare | classify | configure
  const [configSpace, setConfigSpace] = useState(null); // space being configured
  const [configStep, setConfigStep] = useState(0);
  const [copilotOpen, setCopilotOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [venueRes, spacesRes, typesRes, profilesRes] = await Promise.all([
        api.get(`/venues/${venueId}`),
        api.get(`/space-engine/venues/${venueId}/spaces`),
        api.get("/space-engine/activity-types"),
        api.get(`/space-engine/venues/${venueId}/peak-profiles`),
      ]);
      setVenue(venueRes.data);
      setSpaces(spacesRes.data);
      setActivityTypes(typesRes.data);
      setPeakProfiles(profilesRes.data);
    } catch (e) {
      toast.error("Failed to load venue data");
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const goToOverview = () => { setPhase("overview"); setConfigSpace(null); setConfigStep(0); fetchData(); };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="space-manager">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => phase === "overview" ? navigate("/provider/venues") : goToOverview()} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight" data-testid="page-title">
            {phase === "overview" ? "Court & Facility Setup" : phase === "declare" ? "Declare Physical Spaces" : phase === "classify" ? "Classify Spaces" : `Configure: ${configSpace?.space_name || ""}`}
          </h1>
          <p className="text-sm text-muted-foreground">{venue?.name || "Venue"}</p>
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={() => setCopilotOpen(true)} data-testid="copilot-btn">
          <Bot className="h-4 w-4" /> AI CoPilot
        </Button>
      </div>

      {/* Phase Router */}
      {phase === "overview" && (
        <OverviewPhase spaces={spaces} onDeclare={() => setPhase("declare")} onClassify={() => setPhase("classify")}
          onConfigure={(space) => { setConfigSpace(space); setConfigStep(0); setPhase("configure"); }}
          fetchData={fetchData} />
      )}
      {phase === "declare" && (
        <DeclarePhase venueId={venueId} spaces={spaces} setSpaces={setSpaces} onDone={goToOverview} fetchData={fetchData} />
      )}
      {phase === "classify" && (
        <ClassifyPhase spaces={spaces} setSpaces={setSpaces} fetchData={fetchData} onDone={goToOverview} />
      )}
      {phase === "configure" && configSpace && (
        <ConfigurePhase space={configSpace} setSpace={setConfigSpace} activityTypes={activityTypes}
          step={configStep} setStep={setConfigStep} peakProfiles={peakProfiles}
          onDone={goToOverview} fetchData={fetchData} venueId={venueId} />
      )}

      {/* CoPilot Drawer */}
      <CoPilotDrawer open={copilotOpen} onClose={() => setCopilotOpen(false)} venue={venue} spaces={spaces} />
    </div>
  );
}


// ═══════════════════════════════════════════════════
// OVERVIEW PHASE — Dashboard showing all spaces
// ═══════════════════════════════════════════════════
function OverviewPhase({ spaces, onDeclare, onClassify, onConfigure, fetchData }) {
  const unconfigured = spaces.filter(s => !s.classification || s.classification === "unconfigured");
  const configured = spaces.filter(s => s.classification && s.classification !== "unconfigured");

  return (
    <div className="space-y-6" data-testid="overview-phase">
      {/* Action Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="cursor-pointer hover:border-primary/50 transition-colors border-2 border-dashed" onClick={onDeclare} data-testid="add-space-btn">
          <CardContent className="py-6 text-center">
            <Plus className="h-8 w-8 mx-auto mb-2 text-primary" />
            <p className="font-semibold text-sm">Declare Spaces</p>
            <p className="text-xs text-muted-foreground mt-1">Add all your physical courts, rooms & facilities</p>
          </CardContent>
        </Card>
        {unconfigured.length > 0 && (
          <Card className="cursor-pointer hover:border-orange-400/50 transition-colors border-2 border-orange-200 bg-orange-50/30" onClick={onClassify} data-testid="classify-btn">
            <CardContent className="py-6 text-center">
              <Settings className="h-8 w-8 mx-auto mb-2 text-orange-600" />
              <p className="font-semibold text-sm">Classify Spaces</p>
              <p className="text-xs text-muted-foreground mt-1">{unconfigured.length} space{unconfigured.length > 1 ? "s" : ""} need classification</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Space List */}
      {spaces.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-16 text-center">
            <Layers className="h-12 w-12 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-muted-foreground font-medium">No physical spaces declared yet</p>
            <p className="text-sm text-muted-foreground mt-1">Start by declaring all your courts, rooms, and facilities</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <h2 className="text-base font-semibold">Your Spaces ({spaces.length})</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {spaces.map(space => {
              const cls = CLASSIFICATIONS.find(c => c.value === space.classification);
              const Icon = cls?.icon || Layers;
              const isConfigured = space.classification && space.classification !== "unconfigured";
              const hasActivities = (space.activity_configurations?.length || 0) > 0;
              const hasPricing = (space.pricing_policies?.length || 0) > 0;

              return (
                <Card key={space.physical_space_id}
                  className={`cursor-pointer transition-all hover:shadow-md ${isConfigured ? "hover:border-primary/50" : "border-dashed border-orange-300"}`}
                  onClick={() => isConfigured ? onConfigure(space) : onClassify()}
                  data-testid={`space-card-${space.physical_space_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-md border ${cls?.color || "bg-muted text-muted-foreground"}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm leading-tight">{space.space_name}</p>
                          <p className="text-xs text-muted-foreground">{SPACE_TYPES.find(t => t.value === space.space_type)?.label}</p>
                        </div>
                      </div>
                      <Badge variant={space.status === "active" ? "default" : "secondary"} className="text-[10px]" data-testid={`space-status-${space.physical_space_id}`}>
                        {space.status}
                      </Badge>
                    </div>
                    {space.dimensions_length_m > 0 && (
                      <p className="text-xs text-muted-foreground mb-2">{space.dimensions_length_m}m x {space.dimensions_width_m}m &middot; Cap: {space.max_safe_capacity}</p>
                    )}
                    <div className="flex flex-wrap gap-1">
                      <Badge variant="outline" className={`text-[10px] ${cls?.color || ""}`}>{cls?.label || "Unclassified"}</Badge>
                      {hasActivities && <Badge variant="outline" className="text-[10px]">{space.activity_configurations.length} activities</Badge>}
                      {hasPricing && <Badge variant="outline" className="text-[10px]">{space.pricing_policies.length} pricing</Badge>}
                      {(space.discovery_products?.length || 0) > 0 && <Badge variant="outline" className="text-[10px]">{space.discovery_products.length} products</Badge>}
                    </div>
                    {isConfigured && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-primary">
                        <span>Configure</span><ChevronRight className="h-3 w-3" />
                      </div>
                    )}
                    {!isConfigured && (
                      <p className="text-xs text-orange-600 mt-2">Needs classification</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════
// PHASE 1: DECLARE PHYSICAL SPACES
// ═══════════════════════════════════════════════════
function DeclarePhase({ venueId, spaces, setSpaces, onDone, fetchData }) {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editSpace, setEditSpace] = useState(null);
  const emptyForm = {
    space_name: "", space_code: "", space_type: "covered_court", surface_type: "concrete",
    dimensions_length_m: "", dimensions_width_m: "",
    max_safe_capacity: "", notes_internal: "",
  };
  const [form, setForm] = useState(emptyForm);
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openAdd = () => { setForm(emptyForm); setEditSpace(null); setShowForm(true); };
  const openEdit = (space) => {
    setForm({
      space_name: space.space_name || "", space_code: space.space_code || "",
      space_type: space.space_type || "covered_court", surface_type: space.surface_type || "concrete",
      dimensions_length_m: space.dimensions_length_m || "", dimensions_width_m: space.dimensions_width_m || "",
      max_safe_capacity: space.max_safe_capacity || "", notes_internal: space.notes_internal || "",
    });
    setEditSpace(space);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.space_name.trim()) { toast.error("Space name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        dimensions_length_m: parseFloat(form.dimensions_length_m) || 0,
        dimensions_width_m: parseFloat(form.dimensions_width_m) || 0,
        max_safe_capacity: parseInt(form.max_safe_capacity) || 0,
      };
      if (editSpace) {
        await api.put(`/space-engine/spaces/${editSpace.physical_space_id}`, payload);
        toast.success("Space updated");
      } else {
        await api.post(`/space-engine/venues/${venueId}/spaces`, payload);
        toast.success("Space added");
      }
      setShowForm(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (spaceId) => {
    try {
      await api.delete(`/space-engine/spaces/${spaceId}`);
      toast.success("Space removed");
      fetchData();
    } catch {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="space-y-4" data-testid="declare-phase">
      <Card className="bg-blue-50/50 border-blue-200">
        <CardContent className="py-4">
          <p className="text-sm"><strong>Phase 1:</strong> Declare ALL your physical spaces first — every court, room, field, and studio. Don't worry about sport types or pricing yet.</p>
        </CardContent>
      </Card>

      <Button onClick={openAdd} className="gap-2" data-testid="add-space-declare-btn">
        <Plus className="h-4 w-4" /> Add Physical Space
      </Button>

      <div className="grid gap-3 sm:grid-cols-2">
        {spaces.map(space => (
          <Card key={space.physical_space_id} className="group" data-testid={`declare-space-${space.physical_space_id}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">{space.space_name}</p>
                <p className="text-xs text-muted-foreground">
                  {SPACE_TYPES.find(t => t.value === space.space_type)?.label}
                  {space.dimensions_length_m > 0 && ` · ${space.dimensions_length_m}m x ${space.dimensions_width_m}m`}
                  {space.max_safe_capacity > 0 && ` · Cap: ${space.max_safe_capacity}`}
                </p>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="ghost" size="icon" onClick={() => openEdit(space)}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(space.physical_space_id)} data-testid={`delete-space-${space.physical_space_id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onDone} data-testid="done-declare-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Back to Overview</Button>
        {spaces.length > 0 && (
          <Button onClick={onDone} data-testid="proceed-classify-btn">
            Proceed to Classify <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editSpace ? "Edit" : "Add"} Physical Space</DialogTitle></DialogHeader>
          <div className="grid gap-3" data-testid="space-form">
            <div>
              <Label>Space Name *</Label>
              <Input value={form.space_name} onChange={e => up("space_name", e.target.value)} placeholder="e.g. Main Covered Court" data-testid="space-name-input" />
            </div>
            <div>
              <Label>Space Code</Label>
              <Input value={form.space_code} onChange={e => up("space_code", e.target.value)} placeholder="e.g. MCC" data-testid="space-code-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Space Type</Label>
                <Select value={form.space_type} onValueChange={v => up("space_type", v)}>
                  <SelectTrigger data-testid="space-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SPACE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Surface</Label>
                <Select value={form.surface_type} onValueChange={v => up("surface_type", v)}>
                  <SelectTrigger data-testid="surface-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SURFACE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Length (m)</Label><Input type="number" value={form.dimensions_length_m} onChange={e => up("dimensions_length_m", e.target.value)} placeholder="28" data-testid="length-input" /></div>
              <div><Label>Width (m)</Label><Input type="number" value={form.dimensions_width_m} onChange={e => up("dimensions_width_m", e.target.value)} placeholder="15" data-testid="width-input" /></div>
              <div><Label>Capacity</Label><Input type="number" value={form.max_safe_capacity} onChange={e => up("max_safe_capacity", e.target.value)} placeholder="30" data-testid="capacity-input" /></div>
            </div>
            <div><Label>Internal Notes</Label><Input value={form.notes_internal} onChange={e => up("notes_internal", e.target.value)} placeholder="Notes (not shown to players)" /></div>
            <Button onClick={handleSave} disabled={saving} data-testid="save-space-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
              {editSpace ? "Update" : "Add"} Space
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ═══════════════════════════════════════════════════
// PHASE 2: CLASSIFY SPACES
// ═══════════════════════════════════════════════════
function ClassifyPhase({ spaces, setSpaces, fetchData, onDone }) {
  const [suggestions, setSuggestions] = useState({});
  const [loadingSugg, setLoadingSugg] = useState({});
  const [saving, setSaving] = useState({});

  const getSuggestion = async (space) => {
    setLoadingSugg(prev => ({ ...prev, [space.physical_space_id]: true }));
    try {
      const res = await api.post("/space-engine/copilot/suggest-classification", {
        space_name: space.space_name, space_type: space.space_type,
        dimensions_length_m: space.dimensions_length_m, dimensions_width_m: space.dimensions_width_m,
        surface_type: space.surface_type, max_safe_capacity: space.max_safe_capacity,
      });
      setSuggestions(prev => ({ ...prev, [space.physical_space_id]: res.data }));
    } catch {
      toast.error("Failed to get AI suggestion");
    } finally {
      setLoadingSugg(prev => ({ ...prev, [space.physical_space_id]: false }));
    }
  };

  const classify = async (spaceId, classification) => {
    setSaving(prev => ({ ...prev, [spaceId]: true }));
    try {
      // Set classification + derived settings
      const payload = { classification };
      if (classification === "single_sport") {
        payload.split_enabled = false;
        payload.partial_request_enabled = false;
        payload.default_sale_priority = "full_space_priority";
      } else if (classification === "multi_sport") {
        payload.split_enabled = true;
        payload.partial_request_enabled = true;
        payload.default_sale_priority = "full_space_priority";
      } else if (classification === "facility") {
        payload.split_enabled = false;
        payload.partial_request_enabled = false;
        payload.default_sale_priority = "program_priority";
      }
      await api.put(`/space-engine/spaces/${spaceId}`, payload);
      toast.success("Space classified");
      fetchData();
    } catch {
      toast.error("Failed to classify");
    } finally {
      setSaving(prev => ({ ...prev, [spaceId]: false }));
    }
  };

  return (
    <div className="space-y-4" data-testid="classify-phase">
      <Card className="bg-orange-50/50 border-orange-200">
        <CardContent className="py-4">
          <p className="text-sm"><strong>Phase 2:</strong> Classify each space to determine what configuration options are available. The AI CoPilot can help suggest the best type.</p>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {spaces.map(space => {
          const currentCls = CLASSIFICATIONS.find(c => c.value === space.classification);
          const isClassified = space.classification && space.classification !== "unconfigured";
          const sugg = suggestions[space.physical_space_id];

          return (
            <Card key={space.physical_space_id} className={isClassified ? "border-green-200 bg-green-50/20" : ""} data-testid={`classify-card-${space.physical_space_id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{space.space_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {SPACE_TYPES.find(t => t.value === space.space_type)?.label}
                      {space.dimensions_length_m > 0 && ` · ${space.dimensions_length_m}m x ${space.dimensions_width_m}m`}
                      {space.max_safe_capacity > 0 && ` · Cap: ${space.max_safe_capacity}`}
                    </p>
                  </div>
                  {isClassified && <Badge className={currentCls?.color}>{currentCls?.label}</Badge>}
                </div>

                {/* AI Suggestion */}
                {!sugg && !isClassified && (
                  <Button variant="outline" size="sm" className="gap-2" onClick={() => getSuggestion(space)}
                    disabled={loadingSugg[space.physical_space_id]} data-testid={`suggest-btn-${space.physical_space_id}`}>
                    {loadingSugg[space.physical_space_id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bot className="h-3.5 w-3.5" />}
                    Ask AI CoPilot
                  </Button>
                )}
                {sugg && (
                  <Card className="bg-blue-50/50 border-blue-200">
                    <CardContent className="py-3 px-4">
                      <div className="flex items-start gap-2">
                        <Bot className="h-4 w-4 mt-0.5 text-blue-600 shrink-0" />
                        <div>
                          <Badge className="text-[10px] mb-1">{CLASSIFICATIONS.find(c => c.value === sugg.suggestion)?.label}</Badge>
                          <p className="text-xs text-muted-foreground leading-relaxed">{sugg.reason}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Classification Buttons */}
                <div className="grid grid-cols-3 gap-2">
                  {CLASSIFICATIONS.map(cls => {
                    const Icon = cls.icon;
                    const isSelected = space.classification === cls.value;
                    return (
                      <button key={cls.value}
                        onClick={() => classify(space.physical_space_id, cls.value)}
                        disabled={saving[space.physical_space_id]}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${isSelected ? `${cls.color} border-current font-semibold` : "border-muted hover:border-muted-foreground/30"}`}
                        data-testid={`classify-${space.physical_space_id}-${cls.value}`}>
                        <Icon className={`h-5 w-5 mx-auto mb-1 ${isSelected ? "" : "text-muted-foreground"}`} />
                        <p className="text-xs font-medium">{cls.label}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5 hidden sm:block">{cls.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onDone}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
        <Button onClick={onDone} data-testid="done-classify-btn">Done <Check className="h-4 w-4 ml-1" /></Button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════
// PHASE 3: CONFIGURE (per-space wizard)
// ═══════════════════════════════════════════════════
function ConfigurePhase({ space, setSpace, activityTypes, step, setStep, peakProfiles, onDone, fetchData, venueId }) {
  const [saving, setSaving] = useState(false);
  const classification = space.classification || "multi_sport";
  const [partitions, setPartitions] = useState(space.partitions || []);
  const [configs, setConfigs] = useState(space.activity_configurations || []);
  const [pricing, setPricing] = useState(space.pricing_policies || []);
  const [compatRules, setCompatRules] = useState(space.compatibility_rules || []);
  const [recurringUses, setRecurringUses] = useState(space.recurring_reserved_uses || []);
  const [products, setProducts] = useState(space.discovery_products || []);

  // Steps depend on classification
  const STEPS = classification === "single_sport"
    ? [
        { key: "activity", label: "Activity", icon: Layers },
        { key: "pricing", label: "Pricing", icon: DollarSign },
        { key: "recurring", label: "Recurring", icon: CalendarDays },
        { key: "publish", label: "Publish", icon: Eye },
      ]
    : classification === "multi_sport"
    ? [
        { key: "partitions", label: "Partitions", icon: LayoutGrid },
        { key: "activities", label: "Activities", icon: Layers },
        { key: "commercial", label: "Rules", icon: Settings },
        { key: "pricing", label: "Pricing", icon: DollarSign },
        { key: "recurring", label: "Recurring", icon: CalendarDays },
        { key: "publish", label: "Publish", icon: Eye },
      ]
    : [ // facility
        { key: "activities", label: "Activities", icon: Layers },
        { key: "pricing", label: "Pricing", icon: DollarSign },
        { key: "recurring", label: "Recurring", icon: CalendarDays },
        { key: "publish", label: "Publish", icon: Eye },
      ];

  const currentStep = STEPS[step] || STEPS[0];

  // Ensure single-sport has exactly 1 Full partition
  useEffect(() => {
    if (classification === "single_sport" && partitions.length === 0) {
      api.post(`/space-engine/spaces/${space.physical_space_id}/partitions/batch`, {
        partitions: [{ partition_name: "Full Court", partition_code: "FULL", partition_type: "full", relative_area_percent: 100 }]
      }).then(res => setPartitions(res.data)).catch(() => {});
    }
    if (classification === "facility" && partitions.length === 0) {
      api.post(`/space-engine/spaces/${space.physical_space_id}/partitions/batch`, {
        partitions: [{ partition_name: "Full Room", partition_code: "FULL", partition_type: "full", relative_area_percent: 100 }]
      }).then(res => setPartitions(res.data)).catch(() => {});
    }
  }, [classification, partitions.length, space.physical_space_id]);

  // ── Save handlers ──
  const savePartitions = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/space-engine/spaces/${space.physical_space_id}/partitions/batch`, { partitions });
      setPartitions(res.data);
      toast.success("Partitions saved");
    } catch { toast.error("Failed to save partitions"); }
    finally { setSaving(false); }
  };

  const saveActivity = async (config) => {
    setSaving(true);
    try {
      if (config.activity_configuration_id) {
        const res = await api.put(`/space-engine/activity-configurations/${config.activity_configuration_id}`, config);
        setConfigs(prev => prev.map(c => c.activity_configuration_id === config.activity_configuration_id ? res.data : c));
      } else {
        const res = await api.post(`/space-engine/spaces/${space.physical_space_id}/activity-configurations`, config);
        setConfigs(prev => [...prev, res.data]);
      }
      toast.success("Activity saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const deleteActivity = async (id) => {
    await api.delete(`/space-engine/activity-configurations/${id}`);
    setConfigs(prev => prev.filter(c => c.activity_configuration_id !== id));
    toast.success("Removed");
  };

  const savePricing = async (policy) => {
    setSaving(true);
    try {
      if (policy.pricing_policy_id) {
        const res = await api.put(`/space-engine/pricing-policies/${policy.pricing_policy_id}`, policy);
        setPricing(prev => prev.map(p => p.pricing_policy_id === policy.pricing_policy_id ? res.data : p));
      } else {
        const res = await api.post(`/space-engine/spaces/${space.physical_space_id}/pricing-policies`, policy);
        setPricing(prev => [...prev, res.data]);
      }
      toast.success("Pricing saved");
    } catch { toast.error("Failed to save pricing"); }
    finally { setSaving(false); }
  };

  const saveRecurring = async (rru) => {
    setSaving(true);
    try {
      if (rru.recurring_reserved_use_id) {
        const res = await api.put(`/space-engine/recurring-uses/${rru.recurring_reserved_use_id}`, rru);
        setRecurringUses(prev => prev.map(r => r.recurring_reserved_use_id === rru.recurring_reserved_use_id ? res.data : r));
      } else {
        const res = await api.post(`/space-engine/spaces/${space.physical_space_id}/recurring-uses`, rru);
        setRecurringUses(prev => [...prev, res.data]);
      }
      toast.success("Program saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const generateProducts = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/space-engine/spaces/${space.physical_space_id}/discovery-products/generate`);
      setProducts(res.data.products || []);
      toast.success(`${res.data.count} products published`);
    } catch { toast.error("Failed to generate products"); }
    finally { setSaving(false); }
  };

  const activateSpace = async () => {
    try {
      await api.put(`/space-engine/spaces/${space.physical_space_id}`, { status: "active" });
      toast.success("Space is now ACTIVE");
      fetchData();
    } catch { toast.error("Failed to activate"); }
  };

  const handleNext = async () => {
    if (currentStep.key === "partitions") await savePartitions();
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  return (
    <div className="space-y-4" data-testid="configure-phase">
      {/* Step Indicator */}
      <div className="flex gap-1 overflow-x-auto pb-2" data-testid="step-indicator">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <button key={s.key} onClick={() => setStep(i)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${i === step ? "bg-primary text-primary-foreground" : i < step ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
              data-testid={`step-${s.key}`}>
              <Icon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{s.label}</span>
              <span className="sm:hidden">{i + 1}</span>
            </button>
          );
        })}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <currentStep.icon className="h-5 w-5" /> {currentStep.label}
            </CardTitle>
            <CoPilotFieldHelp stepKey={currentStep.key} />
          </div>
          <CardDescription>
            {classification === "single_sport" && "Single-sport court — one activity, full court only"}
            {classification === "multi_sport" && "Multi-sport court — multiple activities with partitions"}
            {classification === "facility" && "Facility — indoor programs and classes"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Active warning */}
          {space.lifecycle_status === "published" && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 mb-4 flex items-start gap-3" data-testid="active-space-warning">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-heading font-bold text-xs text-amber-800 uppercase tracking-wide">This space is currently ACTIVE</p>
                <p className="text-xs text-amber-700 mt-0.5">Changing configuration may affect existing bookings and discovery products. Please validate before saving.</p>
              </div>
            </div>
          )}
          {/* Single Sport: Activity step shows single-select */}
          {currentStep.key === "activity" && (
            <SingleActivityStep configs={configs} partitions={partitions} activityTypes={activityTypes} onSave={saveActivity} onDelete={deleteActivity} saving={saving} />
          )}
          {currentStep.key === "partitions" && (
            <PartitionsStep partitions={partitions} setPartitions={setPartitions} />
          )}
          {currentStep.key === "activities" && (
            <ActivitiesStep configs={configs} partitions={partitions} activityTypes={activityTypes} onSave={saveActivity} onDelete={deleteActivity} saving={saving} classification={classification} />
          )}
          {currentStep.key === "commercial" && (
            <CommercialStep configs={configs} compatRules={compatRules} setCompatRules={setCompatRules} spaceId={space.physical_space_id} />
          )}
          {currentStep.key === "pricing" && (
            <PricingStep policies={pricing} configs={configs} onSave={savePricing} saving={saving} />
          )}
          {currentStep.key === "recurring" && (
            <RecurringStep uses={recurringUses} configs={configs} onSave={saveRecurring}
              onActivate={async (id) => { await api.post(`/space-engine/recurring-uses/${id}/activate`); setRecurringUses(prev => prev.map(r => r.recurring_reserved_use_id === id ? { ...r, status: "active" } : r)); toast.success("Activated"); }}
              onPause={async (id) => { await api.post(`/space-engine/recurring-uses/${id}/pause`); setRecurringUses(prev => prev.map(r => r.recurring_reserved_use_id === id ? { ...r, status: "paused" } : r)); toast.success("Paused"); }}
              saving={saving} />
          )}
          {currentStep.key === "publish" && (
            <PublishStep products={products} onGenerate={generateProducts} onActivate={activateSpace} space={space} saving={saving} />
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" disabled={step === 0} onClick={() => setStep(s => s - 1)} data-testid="prev-step-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={handleNext} disabled={saving} data-testid="next-step-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Save & Next <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={onDone} data-testid="finish-config-btn">
            <Check className="h-4 w-4 mr-1" /> Done
          </Button>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════
// STEP: Single Activity (for single-sport courts)
// ═══════════════════════════════════════════════════
function SingleActivityStep({ configs, partitions, activityTypes, onSave, onDelete, saving }) {
  const existing = configs[0];
  const fullPartition = partitions.find(p => p.partition_type === "full");
  const [activityId, setActivityId] = useState(existing?.activity_type_id || "");
  const [bookingMode, setBookingMode] = useState(existing?.booking_mode || "instant");
  const [slotInterval, setSlotInterval] = useState(existing?.slot_interval_minutes || 60);
  const [maxParticipants, setMaxParticipants] = useState(existing?.max_participants || 14);

  const handleSave = () => {
    if (!activityId) { toast.error("Select an activity"); return; }
    const at = activityTypes.find(t => t.activity_type_id === activityId);
    onSave({
      ...(existing || {}),
      partition_option_id: fullPartition?.partition_option_id,
      activity_type_id: activityId,
      configuration_name: at?.activity_name || activityId,
      public_display_name: at?.activity_name || activityId,
      booking_mode: bookingMode,
      usage_scope: "full_space_exclusive",
      noise_class: at?.default_noise_class || "medium",
      requires_full_space_override: false,
      allowed_peak: true,
      allowed_non_peak: true,
      slot_interval_minutes: slotInterval,
      max_participants: maxParticipants,
      is_publicly_discoverable: true,
      requires_manual_approval: bookingMode === "request_only",
    });
  };

  return (
    <div className="space-y-4" data-testid="single-activity-step">
      <p className="text-sm text-muted-foreground">Choose the single sport/activity for this court. No splits or partitions — full court bookings only.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label>Activity *</Label>
          <Select value={activityId} onValueChange={setActivityId}>
            <SelectTrigger data-testid="single-activity-select"><SelectValue placeholder="Select sport/activity" /></SelectTrigger>
            <SelectContent>{activityTypes.map(a => <SelectItem key={a.activity_type_id} value={a.activity_type_id}>{a.activity_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Booking Mode</Label>
          <Select value={bookingMode} onValueChange={setBookingMode}>
            <SelectTrigger data-testid="single-booking-mode"><SelectValue /></SelectTrigger>
            <SelectContent>{BOOKING_MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Slot Interval (min)</Label>
          <Input type="number" value={slotInterval} onChange={e => setSlotInterval(parseInt(e.target.value) || 60)} data-testid="single-slot-interval" />
        </div>
        <div>
          <Label>Max Participants</Label>
          <Input type="number" value={maxParticipants} onChange={e => setMaxParticipants(parseInt(e.target.value) || 0)} data-testid="single-max-participants" />
        </div>
      </div>
      {bookingMode === "request_only" && (
        <p className="text-xs text-amber-600 font-medium">Request Only mode — all bookings will require your manual approval.</p>
      )}
      <Button onClick={handleSave} disabled={saving} data-testid="save-single-activity-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
        {existing ? "Update" : "Set"} Activity
      </Button>
      {existing && (
        <div className="border rounded-lg p-3 mt-2">
          <p className="text-sm font-medium">{existing.configuration_name}</p>
          <div className="flex gap-2 mt-1">
            <Badge variant="default" className="text-xs">{existing.booking_mode}</Badge>
            <Badge variant="outline" className="text-xs">Full Court</Badge>
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════
// STEP: Partitions (multi-sport only)
// ═══════════════════════════════════════════════════
function PartitionsStep({ partitions, setPartitions }) {
  const addPartition = () => {
    const idx = partitions.length;
    setPartitions(p => [...p, {
      partition_name: `Half ${String.fromCharCode(65 + idx - 1)}`,
      partition_code: `HALF_${String.fromCharCode(65 + idx - 1)}`,
      partition_type: "half", relative_area_percent: 50, is_publicly_exposable: true,
    }]);
  };
  const update = (i, k, v) => setPartitions(p => p.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const remove = (i) => setPartitions(p => p.filter((_, j) => j !== i));

  // Ensure full partition always exists
  const hasFullPartition = partitions.some(p => p.partition_type === "full");

  return (
    <div className="space-y-4" data-testid="partitions-step">
      <p className="text-sm text-muted-foreground">Define how this court can be divided. Always keep a "Full Court" partition, then add half-court or custom splits.</p>
      {!hasFullPartition && (
        <Button variant="outline" size="sm" onClick={() => setPartitions(p => [{ partition_name: "Full Court", partition_code: "FULL", partition_type: "full", relative_area_percent: 100, is_publicly_exposable: true }, ...p])}>
          Add Full Court Partition
        </Button>
      )}
      {partitions.map((p, i) => (
        <div key={i} className="grid gap-3 sm:grid-cols-5 items-end border rounded-lg p-3" data-testid={`partition-${i}`}>
          <div><Label className="text-xs">Name</Label><Input value={p.partition_name} onChange={e => update(i, "partition_name", e.target.value)} /></div>
          <div><Label className="text-xs">Code</Label><Input value={p.partition_code} onChange={e => update(i, "partition_code", e.target.value)} /></div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={p.partition_type} onValueChange={v => update(i, "partition_type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full">Full</SelectItem><SelectItem value="half">Half</SelectItem><SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Area %</Label><Input type="number" value={p.relative_area_percent} onChange={e => update(i, "relative_area_percent", parseInt(e.target.value) || 0)} /></div>
          <div className="flex items-center gap-2">
            {p.partition_type !== "full" && <Button variant="ghost" size="icon" onClick={() => remove(i)} data-testid={`remove-partition-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addPartition} data-testid="add-partition-btn">
        <Plus className="h-4 w-4 mr-1" /> Add Split Partition
      </Button>
    </div>
  );
}


// ═══════════════════════════════════════════════════
// STEP: Activities (multi-sport / facility)
// ═══════════════════════════════════════════════════
function ActivitiesStep({ configs, partitions, activityTypes, onSave, onDelete, saving, classification }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editCfg, setEditCfg] = useState(null);
  const defaultForm = {
    partition_option_id: partitions[0]?.partition_option_id || "",
    activity_type_id: "", configuration_name: "", public_display_name: "",
    booking_mode: "instant", usage_scope: classification === "facility" ? "full_space_exclusive" : "full_space_exclusive",
    noise_class: "medium", safety_class: "medium",
    requires_full_space_override: false, allowed_peak: true, allowed_non_peak: true,
    setup_buffer_minutes: 0, teardown_buffer_minutes: 0,
    min_duration_minutes: 60, max_duration_minutes: 180, slot_interval_minutes: 60,
    max_participants: 14, is_publicly_discoverable: true, requires_manual_approval: false,
  };
  const [form, setForm] = useState(defaultForm);
  const up = (k, v) => {
    setForm(f => {
      const next = { ...f, [k]: v };
      // Auto-enforce: request_only → requires approval
      if (k === "booking_mode" && v === "request_only") next.requires_manual_approval = true;
      return next;
    });
  };
  const openAdd = () => { setForm(defaultForm); setEditCfg(null); setShowAdd(true); };
  const openEdit = (cfg) => { setForm({ ...cfg }); setEditCfg(cfg); setShowAdd(true); };
  const handleSave = () => {
    if (!form.activity_type_id) { toast.error("Select an activity type"); return; }
    const at = activityTypes.find(t => t.activity_type_id === form.activity_type_id);
    const part = partitions.find(p => p.partition_option_id === form.partition_option_id);
    const autoName = `${part?.partition_name || "Full"} ${at?.activity_name || ""}`;
    onSave({ ...form, configuration_name: form.configuration_name || autoName, public_display_name: form.public_display_name || autoName });
    setShowAdd(false);
  };

  return (
    <div className="space-y-4" data-testid="activities-step">
      <p className="text-sm text-muted-foreground">Assign activities to {classification === "facility" ? "this facility" : "each partition"}.</p>
      <Button onClick={openAdd} className="gap-2" size="sm" data-testid="add-activity-btn"><Plus className="h-4 w-4" /> Add Activity</Button>
      {configs.map(cfg => {
        const at = activityTypes.find(t => t.activity_type_id === cfg.activity_type_id);
        const part = partitions.find(p => p.partition_option_id === cfg.partition_option_id);
        return (
          <div key={cfg.activity_configuration_id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`activity-cfg-${cfg.activity_configuration_id}`}>
            <div>
              <p className="font-medium text-sm">{cfg.configuration_name}</p>
              <div className="flex gap-1.5 mt-1">
                <Badge variant="outline" className="text-xs">{part?.partition_name || "Full"}</Badge>
                <Badge variant="outline" className="text-xs">{at?.activity_name || cfg.activity_type_id}</Badge>
                <Badge variant={cfg.booking_mode === "instant" ? "default" : "secondary"} className="text-xs">{cfg.booking_mode}</Badge>
                {cfg.requires_manual_approval && <Badge variant="destructive" className="text-[10px]">Approval Required</Badge>}
              </div>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => openEdit(cfg)}>Edit</Button>
              <Button variant="ghost" size="icon" onClick={() => onDelete(cfg.activity_configuration_id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          </div>
        );
      })}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editCfg ? "Edit" : "Add"} Activity</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            {classification !== "facility" && partitions.length > 1 && (
              <div><Label>Partition</Label>
                <Select value={form.partition_option_id} onValueChange={v => up("partition_option_id", v)}>
                  <SelectTrigger data-testid="cfg-partition-select"><SelectValue placeholder="Select partition" /></SelectTrigger>
                  <SelectContent>{partitions.map(p => <SelectItem key={p.partition_option_id} value={p.partition_option_id}>{p.partition_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Activity Type</Label>
              <Select value={form.activity_type_id} onValueChange={v => up("activity_type_id", v)}>
                <SelectTrigger data-testid="cfg-activity-select"><SelectValue placeholder="Select activity" /></SelectTrigger>
                <SelectContent>{activityTypes.map(a => <SelectItem key={a.activity_type_id} value={a.activity_type_id}>{a.activity_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Display Name</Label><Input value={form.public_display_name} onChange={e => up("public_display_name", e.target.value)} placeholder="What players see" data-testid="cfg-display-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Booking Mode</Label>
                <Select value={form.booking_mode} onValueChange={v => up("booking_mode", v)}>
                  <SelectTrigger data-testid="cfg-booking-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>{BOOKING_MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {classification === "multi_sport" && (
                <div><Label>Usage Scope</Label>
                  <Select value={form.usage_scope} onValueChange={v => up("usage_scope", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="full_space_exclusive">Full Space</SelectItem><SelectItem value="split_eligible">Split Eligible</SelectItem></SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Setup Buffer (min)</Label><Input type="number" value={form.setup_buffer_minutes} onChange={e => up("setup_buffer_minutes", parseInt(e.target.value) || 0)} /></div>
              <div><Label className="text-xs">Teardown (min)</Label><Input type="number" value={form.teardown_buffer_minutes} onChange={e => up("teardown_buffer_minutes", parseInt(e.target.value) || 0)} /></div>
              <div><Label className="text-xs">Slot (min)</Label><Input type="number" value={form.slot_interval_minutes} onChange={e => up("slot_interval_minutes", parseInt(e.target.value) || 0)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Noise</Label><Select value={form.noise_class} onValueChange={v => up("noise_class", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{NOISE_CLASSES.map(n => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Max Participants</Label><Input type="number" value={form.max_participants} onChange={e => up("max_participants", parseInt(e.target.value) || 0)} /></div>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2"><Switch checked={form.allowed_peak} onCheckedChange={v => up("allowed_peak", v)} /><Label className="text-xs">Allowed During Peak</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.allowed_non_peak} onCheckedChange={v => up("allowed_non_peak", v)} /><Label className="text-xs">Allowed Non-Peak</Label></div>
              <div className="flex items-center gap-2">
                <Switch checked={form.requires_manual_approval} disabled={form.booking_mode === "request_only"}
                  onCheckedChange={v => up("requires_manual_approval", v)} data-testid="requires-approval-switch" />
                <Label className="text-xs">{form.booking_mode === "request_only" ? "Approval Required (locked)" : "Requires Approval"}</Label>
              </div>
              <div className="flex items-center gap-2"><Switch checked={form.is_publicly_discoverable} onCheckedChange={v => up("is_publicly_discoverable", v)} /><Label className="text-xs">Publicly Discoverable</Label></div>
            </div>
            {form.booking_mode === "request_only" && (
              <p className="text-xs text-amber-600 font-medium">Request Only mode — approval is automatically required for all bookings.</p>
            )}
            <Button onClick={handleSave} disabled={saving} data-testid="save-activity-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />} Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ═══════════════════════════════════════════════════
// STEP: Commercial Rules
// ═══════════════════════════════════════════════════
function CommercialStep({ configs, compatRules, setCompatRules, spaceId }) {
  const [ruleA, setRuleA] = useState(""); const [ruleB, setRuleB] = useState(""); const [isCompat, setIsCompat] = useState(false); const [saving, setSaving] = useState(false);
  const addRule = async () => {
    if (!ruleA || !ruleB || ruleA === ruleB) { toast.error("Select two different activities"); return; }
    setSaving(true);
    try { const res = await api.post(`/space-engine/spaces/${spaceId}/compatibility-rules`, { activity_configuration_id_a: ruleA, activity_configuration_id_b: ruleB, is_compatible: isCompat, compatibility_type: isCompat ? "allowed" : "disallowed" }); setCompatRules(prev => [...prev, res.data]); toast.success("Rule added"); setRuleA(""); setRuleB(""); }
    catch { toast.error("Failed"); } finally { setSaving(false); }
  };
  const deleteRule = async (id) => { await api.delete(`/space-engine/compatibility-rules/${id}`); setCompatRules(prev => prev.filter(r => r.compatibility_rule_id !== id)); toast.success("Removed"); };

  return (
    <div className="space-y-4" data-testid="commercial-step">
      <p className="text-sm text-muted-foreground">Define which activities can coexist on the same space at the same time.</p>
      <div className="border rounded-lg p-4 space-y-3">
        <Label className="font-medium text-sm">Add Compatibility Rule</Label>
        <div className="grid gap-3 sm:grid-cols-4 items-end">
          <div><Label className="text-xs">Activity A</Label><Select value={ruleA} onValueChange={setRuleA}><SelectTrigger data-testid="compat-a"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{configs.map(c => <SelectItem key={c.activity_configuration_id} value={c.activity_configuration_id}>{c.configuration_name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Activity B</Label><Select value={ruleB} onValueChange={setRuleB}><SelectTrigger data-testid="compat-b"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{configs.map(c => <SelectItem key={c.activity_configuration_id} value={c.activity_configuration_id}>{c.configuration_name}</SelectItem>)}</SelectContent></Select></div>
          <div className="flex items-center gap-2"><Switch checked={isCompat} onCheckedChange={setIsCompat} /><Label className="text-xs">{isCompat ? "Compatible" : "Blocked"}</Label></div>
          <Button onClick={addRule} disabled={saving} size="sm" data-testid="add-compat-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}</Button>
        </div>
      </div>
      {compatRules.map(rule => {
        const a = configs.find(c => c.activity_configuration_id === rule.activity_configuration_id_a);
        const b = configs.find(c => c.activity_configuration_id === rule.activity_configuration_id_b);
        return (<div key={rule.compatibility_rule_id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`compat-rule-${rule.compatibility_rule_id}`}><div className="flex items-center gap-2 text-sm"><span>{a?.configuration_name || "?"}</span><Badge variant={rule.is_compatible ? "default" : "destructive"} className="text-xs">{rule.is_compatible ? "OK" : "Blocked"}</Badge><span>{b?.configuration_name || "?"}</span></div><Button variant="ghost" size="icon" onClick={() => deleteRule(rule.compatibility_rule_id)}><Trash2 className="h-4 w-4 text-destructive" /></Button></div>);
      })}
      {compatRules.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No custom rules. Activities on different partitions can run concurrently by default.</p>}
    </div>
  );
}


// ═══════════════════════════════════════════════════
// STEP: Pricing
// ═══════════════════════════════════════════════════
function PricingStep({ policies, configs, onSave, saving }) {
  const [showAdd, setShowAdd] = useState(false); const [editP, setEditP] = useState(null);
  const emptyForm = { policy_level: "space_level", activity_configuration_id: null, base_rate_amount: 0, currency_code: "PHP", peak_rate_amount: null, non_peak_rate_amount: null, request_rate_amount: null, minimum_acceptable_rate_amount: null, minimum_acceptable_split_total_amount: null, approval_threshold_mode: "none", opportunity_cost_threshold_percent: null };
  const [form, setForm] = useState(emptyForm);
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const openAdd = () => { setForm(emptyForm); setEditP(null); setShowAdd(true); };
  const openEdit = (p) => { setForm({ ...p }); setEditP(p); setShowAdd(true); };
  const handleSave = () => { onSave(form); setShowAdd(false); };

  return (
    <div className="space-y-4" data-testid="pricing-step">
      <p className="text-sm text-muted-foreground">Set rates at space level (benchmark) or per activity. Define peak/non-peak rates.</p>
      <Button onClick={openAdd} className="gap-2" size="sm" data-testid="add-pricing-btn"><Plus className="h-4 w-4" /> Add Pricing</Button>
      {policies.map(p => { const cfg = configs.find(c => c.activity_configuration_id === p.activity_configuration_id); return (
        <div key={p.pricing_policy_id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`pricing-${p.pricing_policy_id}`}>
          <div><p className="font-medium text-sm">{p.policy_level === "space_level" ? "Space Benchmark" : cfg?.configuration_name || "Config"}</p><div className="flex gap-3 mt-1 text-xs text-muted-foreground"><span>Base: PHP {p.base_rate_amount}</span>{p.peak_rate_amount && <span>Peak: {p.peak_rate_amount}</span>}{p.non_peak_rate_amount && <span>Off-Peak: {p.non_peak_rate_amount}</span>}</div></div>
          <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>Edit</Button></div>); })}
      <Dialog open={showAdd} onOpenChange={setShowAdd}><DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>{editP ? "Edit" : "Add"} Pricing</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label>Level</Label><Select value={form.policy_level} onValueChange={v => { up("policy_level", v); if (v === "space_level") up("activity_configuration_id", null); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="space_level">Space Benchmark</SelectItem><SelectItem value="configuration_level">Per Activity</SelectItem></SelectContent></Select></div>
          {form.policy_level === "configuration_level" && (<div><Label>Activity</Label><Select value={form.activity_configuration_id || ""} onValueChange={v => up("activity_configuration_id", v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{configs.map(c => <SelectItem key={c.activity_configuration_id} value={c.activity_configuration_id}>{c.configuration_name}</SelectItem>)}</SelectContent></Select></div>)}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Base Rate (PHP)</Label><Input type="number" value={form.base_rate_amount || ""} onChange={e => up("base_rate_amount", parseFloat(e.target.value) || 0)} data-testid="base-rate-input" /></div>
            <div><Label>Peak Rate</Label><Input type="number" value={form.peak_rate_amount || ""} onChange={e => up("peak_rate_amount", parseFloat(e.target.value) || null)} /></div>
            <div><Label>Non-Peak Rate</Label><Input type="number" value={form.non_peak_rate_amount || ""} onChange={e => up("non_peak_rate_amount", parseFloat(e.target.value) || null)} /></div>
            <div><Label>Request Rate</Label><Input type="number" value={form.request_rate_amount || ""} onChange={e => up("request_rate_amount", parseFloat(e.target.value) || null)} /></div>
          </div>
          <Separator /><p className="text-xs font-medium text-muted-foreground">Revenue Protection</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Min Rate</Label><Input type="number" value={form.minimum_acceptable_rate_amount || ""} onChange={e => up("minimum_acceptable_rate_amount", parseFloat(e.target.value) || null)} /></div>
            <div><Label className="text-xs">Min Split Total</Label><Input type="number" value={form.minimum_acceptable_split_total_amount || ""} onChange={e => up("minimum_acceptable_split_total_amount", parseFloat(e.target.value) || null)} /></div>
          </div>
          <div><Label className="text-xs">Approval Mode</Label><Select value={form.approval_threshold_mode} onValueChange={v => up("approval_threshold_mode", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{APPROVAL_MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent></Select></div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-pricing-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />} Save</Button>
        </div></DialogContent></Dialog>
    </div>
  );
}


// ═══════════════════════════════════════════════════
// STEP: Recurring Programs
// ═══════════════════════════════════════════════════
function RecurringStep({ uses, configs, onSave, onActivate, onPause, saving }) {
  const [showAdd, setShowAdd] = useState(false);
  const DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
  const emptyForm = { activity_configuration_id: "", program_name: "", organizer_name: "", reservation_type: "public_program", start_date: "", end_date: "", start_time: "07:00", end_time: "09:00", days_of_week: [], visibility_mode: "public_listing", commercial_model: "fixed_rate", fixed_rate_amount: 0, blocks_conflicting_inventory: true };
  const [form, setForm] = useState(emptyForm);
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleDay = (d) => setForm(f => ({ ...f, days_of_week: f.days_of_week.includes(d) ? f.days_of_week.filter(x => x !== d) : [...f.days_of_week, d] }));
  const handleSave = () => { if (!form.program_name || !form.activity_configuration_id) { toast.error("Fill required fields"); return; } onSave(form); setShowAdd(false); };

  return (
    <div className="space-y-4" data-testid="recurring-step">
      <p className="text-sm text-muted-foreground">Set up recurring programs that block inventory on a schedule (e.g., Zumba classes, training sessions).</p>
      <Button onClick={() => { setForm(emptyForm); setShowAdd(true); }} className="gap-2" size="sm" data-testid="add-recurring-btn"><Plus className="h-4 w-4" /> Add Program</Button>
      {uses.map(rru => { const cfg = configs.find(c => c.activity_configuration_id === rru.activity_configuration_id); return (
        <div key={rru.recurring_reserved_use_id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`recurring-${rru.recurring_reserved_use_id}`}>
          <div><p className="font-medium text-sm">{rru.program_name}</p><div className="flex flex-wrap gap-1.5 mt-1"><Badge variant="outline" className="text-xs">{cfg?.configuration_name || "?"}</Badge><Badge variant="outline" className="text-xs">{rru.start_time}-{rru.end_time}</Badge><Badge variant="outline" className="text-xs">{rru.days_of_week?.join(", ")}</Badge><Badge variant={rru.status === "active" ? "default" : "secondary"} className="text-xs">{rru.status}</Badge></div></div>
          <div className="flex gap-1">{rru.status === "draft" && <Button size="sm" variant="outline" onClick={() => onActivate(rru.recurring_reserved_use_id)} data-testid={`activate-${rru.recurring_reserved_use_id}`}><Play className="h-3 w-3 mr-1" /> Activate</Button>}{rru.status === "active" && <Button size="sm" variant="outline" onClick={() => onPause(rru.recurring_reserved_use_id)}><Pause className="h-3 w-3 mr-1" /> Pause</Button>}</div>
        </div>); })}
      <Dialog open={showAdd} onOpenChange={setShowAdd}><DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>Add Recurring Program</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label>Program Name</Label><Input value={form.program_name} onChange={e => up("program_name", e.target.value)} placeholder="e.g. Morning Zumba" data-testid="rru-name" /></div>
          <div><Label>Organizer</Label><Input value={form.organizer_name} onChange={e => up("organizer_name", e.target.value)} placeholder="e.g. Coach Maria" /></div>
          <div><Label>Activity</Label><Select value={form.activity_configuration_id} onValueChange={v => up("activity_configuration_id", v)}><SelectTrigger data-testid="rru-cfg-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{configs.map(c => <SelectItem key={c.activity_configuration_id} value={c.activity_configuration_id}>{c.configuration_name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Days</Label><div className="flex flex-wrap gap-2 mt-1">{DAYS.map(d => (<button key={d} onClick={() => toggleDay(d)} className={`px-2 py-1 text-xs rounded border ${form.days_of_week.includes(d) ? "bg-primary text-primary-foreground" : "bg-muted"}`} data-testid={`day-${d}`}>{d.slice(0, 3)}</button>))}</div></div>
          <div className="grid grid-cols-2 gap-3"><div><Label>Start</Label><Input type="time" value={form.start_time} onChange={e => up("start_time", e.target.value)} /></div><div><Label>End</Label><Input type="time" value={form.end_time} onChange={e => up("end_time", e.target.value)} /></div><div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={e => up("start_date", e.target.value)} /></div><div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={e => up("end_date", e.target.value)} /></div></div>
          <div><Label>Rate (PHP)</Label><Input type="number" value={form.fixed_rate_amount || ""} onChange={e => up("fixed_rate_amount", parseFloat(e.target.value) || 0)} /></div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-recurring-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />} Save</Button>
        </div></DialogContent></Dialog>
    </div>
  );
}


// ═══════════════════════════════════════════════════
// STEP: Publish Discovery Products
// ═══════════════════════════════════════════════════
function PublishStep({ products, onGenerate, onActivate, space, saving }) {
  return (
    <div className="space-y-4" data-testid="publish-step">
      <p className="text-sm text-muted-foreground">Generate player-facing products from your configuration. Players see these when browsing your venue.</p>
      <div className="flex gap-3">
        <Button onClick={onGenerate} disabled={saving} className="gap-2" data-testid="generate-products-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Generate Products
        </Button>
        {space?.status !== "active" && (
          <Button onClick={onActivate} className="gap-2 bg-green-600 hover:bg-green-700" data-testid="activate-space-btn">
            <Zap className="h-4 w-4" /> Activate Space
          </Button>
        )}
        {space?.status === "active" && <Badge className="bg-green-100 text-green-700 border-green-300 self-center">ACTIVE</Badge>}
      </div>
      {products.map(p => (
        <div key={p.discovery_product_id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`product-${p.discovery_product_id}`}>
          <div><p className="font-medium text-sm">{p.product_name}</p><div className="flex gap-2 mt-1"><Badge variant={p.product_type === "instant_book" ? "default" : "secondary"} className="text-xs">{p.product_type.replace("_", " ")}</Badge>{p.display_from_price_amount && <span className="text-xs text-muted-foreground">From PHP {p.display_from_price_amount}</span>}</div></div>
          <Badge variant={p.visibility_status === "visible" ? "default" : "secondary"} className="text-xs">{p.visibility_status}</Badge>
        </div>
      ))}
      {products.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Click "Generate" to create discovery products.</p>}
    </div>
  );
}


// ═══════════════════════════════════════════════════
// AI COPILOT DRAWER
// ═══════════════════════════════════════════════════
function CoPilotDrawer({ open, onClose, venue, spaces }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Kumusta! Ako ang SetPlay CoPilot mo. I'll help you set up your courts and facilities. Ano ang gusto mong i-setup?" }
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(`copilot_${Date.now()}`);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setSending(true);
    try {
      const res = await api.post("/space-engine/copilot", {
        message: userMsg,
        session_id: sessionId.current,
        venue_context: { name: venue?.name, city: venue?.city },
        spaces_context: spaces.map(s => ({
          space_name: s.space_name, space_type: s.space_type,
          dimensions_length_m: s.dimensions_length_m, dimensions_width_m: s.dimensions_width_m,
          classification: s.classification,
        })),
      });
      setMessages(prev => [...prev, { role: "assistant", text: res.data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", text: "Sorry, may error. Try again!" }]);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-background border-l shadow-xl z-50 flex flex-col" data-testid="copilot-drawer">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-primary/5">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-primary" />
          <div>
            <p className="font-semibold text-sm">SetPlay CoPilot</p>
            <p className="text-[10px] text-muted-foreground">TagLish AI Court Setup Assistant</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-copilot-btn"><X className="h-4 w-4" /></Button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="copilot-messages">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed ${msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
              <p className="whitespace-pre-wrap">{msg.text}</p>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-xl px-3 py-2"><Loader2 className="h-4 w-4 animate-spin" /></div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-3 border-t">
        <div className="flex gap-2">
          <Input value={input} onChange={e => setInput(e.target.value)}
            placeholder="Ask about court setup..."
            onKeyDown={e => e.key === "Enter" && send()}
            data-testid="copilot-input" />
          <Button onClick={send} disabled={sending || !input.trim()} size="icon" data-testid="copilot-send-btn">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
